/* eslint-disable react-refresh/only-export-components -- component + tightly-coupled factory helpers */

import { useThree } from "@react-three/fiber";
import { type RefObject, useCallback, useEffect, useRef } from "react";
import * as THREE from "three";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import type { InteriorLayout } from "../../domain/interior";
import { UNIT_BOX } from "../../engines/geometry";
import type { BuildInput } from "../../engines/types";
import { emitter } from "../../lib/eventBus";
import {
	InteriorDragController,
	type InteriorDragPreview,
	type InteriorDragTarget,
} from "./InteriorDragController";
import type { InteriorEditSetup } from "./interiorHandles";
import {
	INTERACTION_HOVER_OPACITY,
	INTERACTION_LOCKED_OPACITY,
	type MeasurementPickLayer,
	type MeasurementTarget,
} from "./measurementTargets";
import { OpeningDragController } from "./OpeningDragController";
import { PartitionDragController } from "./PartitionDragController";
import type { OrbitControlsRef } from "./SceneControls";
import type { PartitionMeasurementPreview } from "./viewerMeasurements";

interface MeasurementPointer {
	x: number;
	y: number;
}

export interface MeasurementHoverState {
	target: MeasurementTarget;
	pointer: MeasurementPointer;
}

interface ViewerMeasurementControllerProps {
	buildInput: BuildInput;
	controlsRef: RefObject<OrbitControlsRef | null>;
	disabled?: boolean;
	/**
	 * The rotated sauna rig. Handed to the drag controllers so their pick
	 * planes and ghosts inherit the building's orientation. Picking itself is
	 * world-space raycasting against real meshes, so it needs no fixing.
	 */
	rigRef?: RefObject<THREE.Object3D | null>;
	onHoverChange?: (state: MeasurementHoverState | null) => void;
	onSelectionChange?: (target: MeasurementTarget | null) => void;
	onOpeningMoved?: (opening: WallOpening) => void;
	onPartitionSplitChange?: (partitionSplit: number) => void;
	onPartitionDragPreviewChange?: (
		preview: PartitionMeasurementPreview | null,
	) => void;
	/**
	 * Derived once by `SceneCanvas` and shared with `InteriorEditHandles`, so the
	 * handles the user grabs and the geometry a drag resolves against are the same
	 * objects rather than two derivations that agree today. Null when the design has
	 * no layout-driven interior (the deprecated barrel).
	 */
	interiorEdit?: InteriorEditSetup | null;
	onInteriorLayoutChange?: (layout: InteriorLayout) => void;
	onInteriorDragPreviewChange?: (preview: InteriorDragPreview | null) => void;
}

const DRAG_THRESHOLD_PX = 8;

/**
 * Do two targets carry the same payload?
 *
 * One level deep is enough: every `MeasurementTarget` field is a primitive except
 * `planTarget`, which is fully determined by `id`. Used to decide whether a
 * post-rebuild re-bind has to notify the parent.
 */
function shallowTargetsEqual(
	a: MeasurementTarget,
	b: MeasurementTarget,
): boolean {
	if (a === b) return true;
	if (a.kind !== b.kind) return false;
	const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
	for (const key of keys) {
		if (key === "planTarget") continue;
		const left = (a as unknown as Record<string, unknown>)[key];
		const right = (b as unknown as Record<string, unknown>)[key];
		if (left !== right) return false;
	}
	return true;
}

function getPointerDistance(
	start: MeasurementPointer,
	current: MeasurementPointer,
) {
	const dx = current.x - start.x;
	const dy = current.y - start.y;
	return Math.hypot(dx, dy);
}

const HOVER_FRAME_NAME = "detail/interaction/hover-frame";
/** Border thickness (m) of the selection frame. */
const HOVER_FRAME_BORDER_M = 0.014;
/**
 * Strip depth (m) along the handle's facade normal. Kept flat —
 * the frame renders with depthTest off, so it doesn't need to
 * physically clear the wall assembly, and a deep strip would read
 * as a chunky 3D bar at oblique camera angles.
 */
const HOVER_FRAME_DEPTH_M = 0.01;

/**
 * Add/remove a terracotta border frame around an opening/partition
 * handle: four thin strips hugging the handle's perimeter. A frame
 * (not an inverted hull / fill) because openings contain glass and
 * doorway cutouts that don't write depth, so any backface-based
 * outline bleeds through them as a solid fill. depthTest stays on so
 * occluded handles stay occluded
 * (per measurement-picker-must-respect-occlusion). Walls are
 * excluded — outlining a whole facade reads as a glitch; they keep
 * the opacity overlay only.
 *
 * Eligibility is `userData.hoverFrame` (or the legacy `interactionKind`, which
 * `interactionOverlays` still sets and which doubles as `DragController`'s dispatch
 * key). The two are separate on purpose: a handle can want the ring without opting
 * into a dispatcher that has no branch for it.
 */
function syncHoverFrame(object: THREE.Object3D, active: boolean) {
	if (!object.userData?.hoverFrame && !object.userData?.interactionKind) return;
	const existing = object.getObjectByName(HOVER_FRAME_NAME);

	if (!active) {
		if (existing) {
			object.remove(existing);
			existing.traverse((child) => {
				if (
					child instanceof THREE.Mesh &&
					child.material instanceof THREE.Material
				) {
					child.material.dispose();
				}
			});
		}
		return;
	}
	if (existing || !(object instanceof THREE.Mesh)) return;

	const params = (object.geometry as THREE.BoxGeometry).parameters;
	if (!params?.width || !params?.height || !params?.depth) return;

	// LOCAL extents, and the scale that turns them into metres. A handle built from
	// the shared `UNIT_BOX` carries its real size in `mesh.scale`, so reading
	// `parameters` alone would size every ring 1 x 1 x 1 and pick the wrong normal
	// axis. The strips are children, so their own numbers stay in local units —
	// which means the constants below, which ARE metres, divide by the scale.
	const local = [params.width, params.height, params.depth];
	const scaleOf = [object.scale.x, object.scale.y, object.scale.z];
	const sizes = local.map((value, axis) => value * scaleOf[axis]);
	/** A world-space length, expressed in this mesh's local units. */
	const toLocal = (metres: number, axis: number) =>
		scaleOf[axis] === 0 ? 0 : metres / scaleOf[axis];

	// The handle's thinnest axis is its facade normal (0.08 m for
	// openings, regardless of which wall they sit on). The other two
	// axes span the opening; the frame rings those.
	const normalAxis = sizes.indexOf(Math.min(...sizes));
	const [uAxis, vAxis] = [0, 1, 2].filter((axis) => axis !== normalAxis);

	// One material for all four strips; SaunaModel's disposal traverse
	// may dispose it repeatedly, which three tolerates. depthTest off:
	// the ring zone around an opening is covered by trim boards that
	// sit proud of the cladding, and occluded handles can't be hovered
	// in the first place (picking respects occlusion), so drawing the
	// frame on top is safe and keeps it visible over trim.
	const material = new THREE.MeshBasicMaterial({
		color: 0xe8873c,
		toneMapped: false,
		transparent: true,
		opacity: 0.95,
		depthTest: false,
		depthWrite: false,
	});

	const frame = new THREE.Group();
	frame.name = HOVER_FRAME_NAME;

	const addStrip = (
		flankAxis: number,
		runAxis: number,
		runLengthM: number,
		side: 1 | -1,
	) => {
		const scale: [number, number, number] = [1, 1, 1];
		scale[runAxis] = toLocal(runLengthM, runAxis);
		scale[flankAxis] = toLocal(HOVER_FRAME_BORDER_M, flankAxis);
		scale[normalAxis] = toLocal(HOVER_FRAME_DEPTH_M, normalAxis);
		const position: [number, number, number] = [0, 0, 0];
		position[flankAxis] =
			side *
			(local[flankAxis] / 2 + toLocal(HOVER_FRAME_BORDER_M, flankAxis) / 2);

		const strip = new THREE.Mesh(UNIT_BOX, material);
		strip.scale.set(...scale);
		strip.position.set(...position);
		strip.renderOrder = 998;
		// The frame must never swallow the raycast that keeps it alive.
		strip.raycast = () => {};
		frame.add(strip);
	};

	const b = HOVER_FRAME_BORDER_M;
	addStrip(vAxis, uAxis, sizes[uAxis] + 2 * b, 1);
	addStrip(vAxis, uAxis, sizes[uAxis] + 2 * b, -1);
	addStrip(uAxis, vAxis, sizes[vAxis], 1);
	addStrip(uAxis, vAxis, sizes[vAxis], -1);

	object.add(frame);
}

function getMaterialOpacity(
	object: THREE.Object3D | null,
	active: "hover" | "locked" | null,
) {
	if (
		!(object instanceof THREE.Mesh) ||
		!(object.material instanceof THREE.MeshBasicMaterial)
	) {
		return;
	}

	syncHoverFrame(object, active !== null);

	if (active === "locked") {
		object.material.opacity =
			object.userData.baseOpacityLocked ?? INTERACTION_LOCKED_OPACITY;
		return;
	}

	if (active === "hover") {
		object.material.opacity =
			object.userData.baseOpacityHover ?? INTERACTION_HOVER_OPACITY;
		return;
	}

	object.material.opacity = object.userData.baseOpacity ?? 0;
}

export function ViewerMeasurementController({
	buildInput,
	controlsRef,
	disabled = false,
	rigRef,
	onHoverChange,
	onSelectionChange,
	onOpeningMoved,
	onPartitionSplitChange,
	onPartitionDragPreviewChange,
	interiorEdit = null,
	onInteriorLayoutChange,
	onInteriorDragPreviewChange,
}: ViewerMeasurementControllerProps) {
	const scene = useThree((state) => state.scene);
	const camera = useThree((state) => state.camera);
	const gl = useThree((state) => state.gl);
	const invalidate = useThree((state) => state.invalidate);

	const raycasterRef = useRef(new THREE.Raycaster());
	const mouseRef = useRef(new THREE.Vector2());
	const dragControllerRef = useRef<
		| OpeningDragController
		| PartitionDragController
		| InteriorDragController
		| null
	>(null);
	// Set while a decorative HTML handle (PartitionDragAffordance's partition
	// pill or door/window pills) owns a gesture, via the
	// 'partition:handleDrag' / 'opening:handleDrag' bus events. The pointer
	// handlers below stand down so the two pointer owners never both act on
	// the same drag. A plain ref (read at event time) keeps it out of
	// effect deps.
	const handleDragActiveRef = useRef(false);
	const hoverObjectRef = useRef<THREE.Object3D | null>(null);
	const lockedObjectRef = useRef<THREE.Object3D | null>(null);
	const pointerDownRef = useRef<{
		target: MeasurementTarget;
		object: THREE.Object3D;
		pointer: MeasurementPointer;
	} | null>(null);
	const activeSelectionRef = useRef<MeasurementTarget | null>(null);

	const setControlsEnabled = useCallback(
		(enabled: boolean) => {
			if (controlsRef.current) {
				controlsRef.current.enabled = enabled;
			}
		},
		[controlsRef],
	);

	/** Which layer of handles the pointer may pick right now. */
	const activePickLayer: MeasurementPickLayer =
		interiorEdit !== null ? "interior" : "shell";

	const getMeasurementObjects = useCallback(() => {
		const targets: THREE.Object3D[] = [];
		scene.traverseVisible((child) => {
			const target = child.userData?.measurementTarget as
				| MeasurementTarget
				| undefined;
			if (!target) return;
			// Distance stays the only tiebreaker (2026-04-29 note); what changes with
			// the mode is WHICH handles are candidates. See `MeasurementPickLayer`.
			if ((target.pickLayer ?? "shell") !== activePickLayer) return;
			targets.push(child);
		});
		return targets;
	}, [activePickLayer, scene]);

	const pickTarget = useCallback(
		(event: PointerEvent) => {
			const rect = gl.domElement.getBoundingClientRect();
			const pointer = {
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
			};

			mouseRef.current.x = (pointer.x / rect.width) * 2 - 1;
			mouseRef.current.y = -(pointer.y / rect.height) * 2 + 1;
			raycasterRef.current.setFromCamera(
				mouseRef.current,
				camera as THREE.PerspectiveCamera,
			);

			const intersections = raycasterRef.current.intersectObjects(
				getMeasurementObjects(),
				false,
			);
			let best: { target: MeasurementTarget; object: THREE.Object3D } | null =
				null;

			for (const hit of intersections) {
				const target = hit.object.userData?.measurementTarget as
					| MeasurementTarget
					| undefined;
				if (!target) continue;
				best = { target, object: hit.object };
				break;
			}

			return {
				pointer,
				hit: best,
			};
		},
		[camera, getMeasurementObjects, gl.domElement],
	);

	const updateHover = useCallback(
		(object: THREE.Object3D | null, state: MeasurementHoverState | null) => {
			if (hoverObjectRef.current === object) {
				onHoverChange?.(state);
				return;
			}

			if (
				hoverObjectRef.current &&
				hoverObjectRef.current !== lockedObjectRef.current
			) {
				getMaterialOpacity(hoverObjectRef.current, null);
			}

			hoverObjectRef.current = object;

			if (object && object !== lockedObjectRef.current) {
				getMaterialOpacity(object, "hover");
			}

			onHoverChange?.(state);
			invalidate();
		},
		[invalidate, onHoverChange],
	);

	const setLockedSelection = useCallback(
		(
			object: THREE.Object3D | null,
			target: MeasurementTarget | null,
			notify = true,
		) => {
			if (
				lockedObjectRef.current &&
				lockedObjectRef.current !== hoverObjectRef.current
			) {
				getMaterialOpacity(lockedObjectRef.current, null);
			}

			lockedObjectRef.current = object;
			activeSelectionRef.current = target;

			if (object) {
				getMaterialOpacity(object, "locked");
			}

			if (!object && hoverObjectRef.current) {
				getMaterialOpacity(hoverObjectRef.current, "hover");
			}

			if (notify) {
				onSelectionChange?.(target);
			}
			invalidate();
		},
		[invalidate, onSelectionChange],
	);

	const cleanupDrag = useCallback(() => {
		dragControllerRef.current?.dispose();
		dragControllerRef.current = null;
		setControlsEnabled(true);
		onPartitionDragPreviewChange?.(null);
		onInteriorDragPreviewChange?.(null);
		invalidate();
	}, [
		invalidate,
		onInteriorDragPreviewChange,
		onPartitionDragPreviewChange,
		setControlsEnabled,
	]);

	// Stand down while a partition or opening HTML handle drives its own
	// drag, so hover picking and cursor updates don't fight the active
	// gesture. Both bus events share this handler — only one pill can own a
	// gesture at a time, so a single ref is enough to track either.
	useEffect(() => {
		const onHandleDrag = (active: boolean) => {
			handleDragActiveRef.current = active;
			if (active) {
				updateHover(null, null);
			}
		};
		emitter.on("partition:handleDrag", onHandleDrag);
		emitter.on("opening:handleDrag", onHandleDrag);
		emitter.on("rotation:handleDrag", onHandleDrag);
		return () => {
			emitter.off("partition:handleDrag", onHandleDrag);
			emitter.off("opening:handleDrag", onHandleDrag);
			emitter.off("rotation:handleDrag", onHandleDrag);
			handleDragActiveRef.current = false;
		};
	}, [updateHover]);

	useEffect(() => {
		if (disabled) {
			updateHover(null, null);
			return undefined;
		}

		const domElement = gl.domElement;

		const maybeStartDrag = (event: PointerEvent) => {
			const pointerDown = pointerDownRef.current;
			if (
				!pointerDown ||
				dragControllerRef.current ||
				!pointerDown.target.draggable
			) {
				return false;
			}

			if (
				getPointerDistance(pointerDown.pointer, {
					x: event.clientX,
					y: event.clientY,
				}) < DRAG_THRESHOLD_PX
			) {
				return false;
			}

			setControlsEnabled(false);

			if (pointerDown.target.kind === "opening" && onOpeningMoved) {
				const { openingId } = pointerDown.target;
				const opening = buildInput.openings.find(
					(candidate) => candidate.id === openingId,
				);
				if (!opening) {
					setControlsEnabled(true);
					return false;
				}

				const controller = new OpeningDragController({
					scene,
					parent: rigRef?.current ?? undefined,
					camera: camera as THREE.PerspectiveCamera,
					renderer: gl,
					dimensions: buildInput.dimensions,
					existingOpenings: buildInput.openings,
					overrides: buildInput.overrides,
					partitionSplit: buildInput.partitionSplit,
 hasRelaxRoom: buildInput.hasRelaxRoom ?? buildInput.partitionSplit !== undefined,
 saunaPosition: buildInput.saunaPosition,
 roofConfig: buildInput.roofConfig,
 wallHeightM: buildInput.wallHeightM,
					onCommit: (nextOpening) => {
						onOpeningMoved(nextOpening);
						cleanupDrag();
					},
					onCancel: cleanupDrag,
					onDragUpdate: () => invalidate(),
				});
				controller.startMoveDrag(opening);
				dragControllerRef.current = controller;
				pointerDownRef.current = null;
				return true;
			}

			// Lavar and the aggregat. One controller for all three kinds: what
			// differs between them is a `PlanDragTarget`, and every rule the drag
			// answers to lives in `resolvePlanDrag`, not here.
			// The handle carries what a drag on it moves, so there is nothing to
			// decode: the id format and the kind→target mapping have one owner, the
			// factory that built the handle.
			const planTarget: InteriorDragTarget | null =
				"planTarget" in pointerDown.target
					? pointerDown.target.planTarget
					: null;
			if (planTarget && interiorEdit && onInteriorLayoutChange) {
				const controller = new InteriorDragController({
					parent: rigRef?.current ?? scene,
					camera: camera as THREE.PerspectiveCamera,
					renderer: gl,
					target: planTarget,
					setup: interiorEdit,
					// The gesture is anchored on the POINTERDOWN, not on this move —
					// anchoring on the threshold-crossing move would shift the element
					// by ~8 px worth of millimetres on every grab.
					anchorClient: pointerDown.pointer,
					onCommit: (layout) => {
						onInteriorLayoutChange(layout);
						cleanupDrag();
					},
					onCancel: cleanupDrag,
					onPreviewChange: (preview) => {
						onInteriorDragPreviewChange?.(preview);
						invalidate();
					},
					onDragUpdate: () => invalidate(),
				});
				controller.startDrag(event.pointerId);
				dragControllerRef.current = controller;
				pointerDownRef.current = null;
				return true;
			}

			if (pointerDown.target.kind === "partition" && onPartitionSplitChange) {
				onPartitionDragPreviewChange?.({
					partitionSplit: buildInput.partitionSplit ?? 0.5,
					saunaDepthMm: Math.round(
						(buildInput.partitionSplit ?? 0.5) *
							buildInput.dimensions.length *
							1000,
					),
					loungeDepthMm: Math.max(
						0,
						Math.round(
							(1 - (buildInput.partitionSplit ?? 0.5)) *
								buildInput.dimensions.length *
								1000,
						),
					),
					dividerZ:
						-buildInput.dimensions.length / 2 +
						buildInput.dimensions.length * (buildInput.partitionSplit ?? 0.5),
					valid: true,
				});
				const controller = new PartitionDragController({
					scene,
					parent: rigRef?.current ?? undefined,
					camera: camera as THREE.PerspectiveCamera,
					renderer: gl,
					dimensions: buildInput.dimensions,
					partitionSplit: buildInput.partitionSplit ?? 0.5,
					heaterType: buildInput.thermalInputs?.heaterType,
					heaterModelId: buildInput.thermalInputs?.heaterModelId,
					wallHeightM: buildInput.wallHeightM,
					onCommit: (nextSplit) => {
						onPartitionSplitChange(nextSplit);
						cleanupDrag();
					},
					onCancel: cleanupDrag,
					onDragUpdate: () => invalidate(),
					onPreviewChange: onPartitionDragPreviewChange,
				});
				controller.startDrag();
				dragControllerRef.current = controller;
				pointerDownRef.current = null;
				return true;
			}

			setControlsEnabled(true);
			return false;
		};

		const handlePointerMove = (event: PointerEvent) => {
			if (dragControllerRef.current || handleDragActiveRef.current) return;

			if (maybeStartDrag(event)) {
				return;
			}

			const { hit, pointer } = pickTarget(event);
			updateHover(
				hit?.object ?? null,
				hit
					? {
							target: hit.target,
							pointer,
						}
					: null,
			);
			domElement.style.cursor = hit?.target.draggable
				? "grab"
				: hit
					? "pointer"
					: "";
		};

		const handlePointerDown = (event: PointerEvent) => {
			if (dragControllerRef.current || handleDragActiveRef.current) return;

			const { hit } = pickTarget(event);
			if (!hit) {
				pointerDownRef.current = null;
				setLockedSelection(null, null);
				return;
			}

			pointerDownRef.current = {
				target: hit.target,
				object: hit.object,
				pointer: { x: event.clientX, y: event.clientY },
			};
		};

		const handlePointerUp = (event: PointerEvent) => {
			if (dragControllerRef.current || handleDragActiveRef.current) return;

			const pointerDown = pointerDownRef.current;
			pointerDownRef.current = null;
			if (!pointerDown) return;

			const { hit } = pickTarget(event);
			if (!hit) {
				return;
			}

			if (hit.target.id !== pointerDown.target.id) {
				return;
			}

			if (
				getPointerDistance(pointerDown.pointer, {
					x: event.clientX,
					y: event.clientY,
				}) >= DRAG_THRESHOLD_PX
			) {
				return;
			}

			setLockedSelection(hit.object, hit.target);
			updateHover(null, null);
		};

		const handlePointerLeave = () => {
			domElement.style.cursor = "";
			if (dragControllerRef.current || lockedObjectRef.current) return;
			updateHover(null, null);
		};

		domElement.addEventListener("pointermove", handlePointerMove);
		domElement.addEventListener("pointerdown", handlePointerDown);
		domElement.addEventListener("pointerup", handlePointerUp);
		domElement.addEventListener("pointerleave", handlePointerLeave);

		return () => {
			domElement.removeEventListener("pointermove", handlePointerMove);
			domElement.removeEventListener("pointerdown", handlePointerDown);
			domElement.removeEventListener("pointerup", handlePointerUp);
			domElement.removeEventListener("pointerleave", handlePointerLeave);
			domElement.style.cursor = "";
			pointerDownRef.current = null;
			updateHover(null, null);
			cleanupDrag();
		};
	}, [
		buildInput.dimensions,
		buildInput.openings,
		buildInput.overrides,
		buildInput.partitionSplit,
		buildInput.thermalInputs?.heaterType,
		buildInput.thermalInputs?.heaterModelId,
		buildInput.wallHeightM,
		camera,
		cleanupDrag,
		disabled,
		gl,
		interiorEdit,
		invalidate,
		onInteriorDragPreviewChange,
		onInteriorLayoutChange,
		onOpeningMoved,
		onPartitionDragPreviewChange,
		onPartitionSplitChange,
		pickTarget,
		rigRef,
		scene,
		setControlsEnabled,
		setLockedSelection,
		updateHover,
	]);

	useEffect(() => {
		if (!activeSelectionRef.current) {
			return;
		}

		const current = activeSelectionRef.current;
		if (current.kind === "opening") {
			const opening = buildInput.openings.find(
				(candidate) => candidate.id === current.openingId,
			);
			if (!opening) {
				setLockedSelection(null, null);
			}
		}
		// ONE rule for "this lock belongs to a layer you can no longer pick".
		//
		// Both directions of the mode switch land here, and the layer is what makes it
		// symmetric — a kind list only ever covered the direction I happened to think
		// of. Opening the mode has to clear a locked WALL or OPENING: picking has moved
		// to the interior layer, but `Viewer3D` prefers `lockedMeasurement` over the
		// hovered target, so hovering a lav kept showing the wall's readout — and the
		// shell handle kept its locked tint (an opening also keeps its hover ring,
		// since `interactionOverlays` marks those with `interactionKind`) while its
		// layer was supposed to have stood down. Closing the mode has to clear a locked
		// bench or aggregat for the mirror reason; clearing is also what disposes the
		// ring `syncHoverFrame` attached, since `InteriorEditHandles` carries
		// `dispose={null}` and R3F frees nothing there.
		//
		// This is the same generalisation the picker itself needed: filter on the
		// LAYER, never on an enumeration of kinds.
		if ((current.pickLayer ?? "shell") !== activePickLayer) {
			setLockedSelection(null, null);
			return;
		}

		// Within the interior layer a run can additionally be DROPPED by reconciliation
		// (shortened below the minimum, or squeezed out by a dimension change) — the
		// same staleness the opening branch above guards. The aggregat needs no
		// equivalent: it exists for as long as the layout does.
		if (current.kind === "bench" || current.kind === "bench-edge") {
			const runs = interiorEdit?.model.layout.benchRuns ?? [];
			if (!runs.some((run) => run.id === current.runId)) {
				setLockedSelection(null, null);
			}
		}

		// A merely HOVERED handle needs no equivalent anywhere: `interiorEdit` is a
		// dependency of the pointer effect below, so either switch re-runs it and its
		// cleanup already calls `updateHover(null, null)`. Verified by removing both —
		// the hover test only fails when neither exists.
	}, [activePickLayer, buildInput.openings, interiorEdit, setLockedSelection]);

	/*
	 * `buildInput` is a rebuild TRIGGER here, not a value the body reads. A
	 * committed edit replaces the scene's meshes, so the locked object reference
	 * points at a disposed mesh and has to be re-resolved by target id — dropping
	 * the dependency would leave the selection attached to geometry that no longer
	 * exists. Widening the `MeasurementTarget` union is what made the rule start
	 * reporting this pre-existing pattern; the pattern itself is unchanged.
	 */
	// biome-ignore lint/correctness/useExhaustiveDependencies: rebuild trigger, see above
	useEffect(() => {
		const activeSelection = activeSelectionRef.current;
		if (!activeSelection) {
			return;
		}

		let nextObject: THREE.Object3D | null = null;
		let nextTarget: MeasurementTarget | null = null;
		scene.traverse((child) => {
			if (nextObject) return;
			const target = child.userData?.measurementTarget as
				| MeasurementTarget
				| undefined;
			if (target?.id === activeSelection.id) {
				nextObject = child;
				nextTarget = target;
			}
		});

		if (!nextObject) {
			return;
		}

		// Re-bind to the REPLACEMENT target's payload, not the old one.
		//
		// This matters because the interior readouts render their millimetres straight
		// off the target (`lengthMm`, `startMm`, `offsetMm`). Rebinding the new mesh
		// while keeping the pre-drag payload — and with `notify: false`, so the parent
		// never heard about it either — left a locked bench card showing the length it
		// had before the drag that just changed it. Wall and opening targets read their
		// numbers from `buildInput` at render time, which is why this never showed
		// before the interior kinds existed.
		const replacement: MeasurementTarget = nextTarget ?? activeSelection;
		const changed = !shallowTargetsEqual(replacement, activeSelection);
		setLockedSelection(nextObject, replacement, changed);
	}, [buildInput, scene, setLockedSelection]);

	return null;
}

// The target types and their factories live in `measurementTargets.ts` (a leaf, so
// the handle builders can use them without importing this component). Re-exported
// here because every existing consumer imports them from this module.
export * from "./measurementTargets";
